/**
 * Estado de rota compartilhado para `/questionnaire-respond` no portal paciente (legado).
 * Navegação a partir da lista de tasks / HeaderNotification deve enviar esta forma.
 */

export interface QuestionnaireRespondLegacyLocationState {
  questionnaireId: string;
  questionnaireTitle: string;
  subjectReference: string;
  patientTaskId: string;
  requesterPractitionerRoleReference: string;
  ownerOrganizationReference: string;
  /** Derivado de `Task.basedOn` quando contém `Appointment/...`; omitido nos demais fluxos. */
  appointmentReference?: string;
  /**
   * Patient do responsável logado que envia a resposta quando `subjectReference` é o dependente.
   * Persistido como `QuestionnaireResponse.author` no POST.
   */
  authorReference?: string;
  /** Nome exibido na UI (“Respondendo em nome de …”). */
  subjectPatientDisplayName?: string;
}

function isNonEmptyString(value: unknown): value is string {
  return typeof value === "string" && value.trim().length > 0;
}

function isOptionalAppointmentReferenceField(value: unknown): boolean {
  if (value === undefined) return true;
  if (typeof value !== "string") return false;
  const trimmed = value.trim();
  return (
    trimmed.startsWith("Appointment/") &&
    trimmed.length > "Appointment/".length
  );
}

function isOptionalPatientReferenceField(value: unknown): boolean {
  if (value === undefined) return true;
  if (typeof value !== "string") return false;
  const trimmed = value.trim();
  return (
    trimmed.startsWith("Patient/") && trimmed.length > "Patient/".length
  );
}

function isOptionalDisplayNameField(value: unknown): boolean {
  if (value === undefined) return true;
  return typeof value === "string";
}

export function isQuestionnaireRespondLegacyLocationState(
  value: unknown,
): value is QuestionnaireRespondLegacyLocationState {
  if (!value || typeof value !== "object") return false;
  const candidate = value as Record<string, unknown>;
  return (
    isNonEmptyString(candidate.questionnaireId) &&
    isNonEmptyString(candidate.questionnaireTitle) &&
    isNonEmptyString(candidate.subjectReference) &&
    isNonEmptyString(candidate.patientTaskId) &&
    isNonEmptyString(candidate.requesterPractitionerRoleReference) &&
    isNonEmptyString(candidate.ownerOrganizationReference) &&
    isOptionalAppointmentReferenceField(candidate.appointmentReference) &&
    isOptionalPatientReferenceField(candidate.authorReference) &&
    isOptionalDisplayNameField(candidate.subjectPatientDisplayName)
  );
}
